import {
  EoaInfoConfigurationPayload,
  EoaSurveyConfigurationPayload,
  SurveyLanguageOption,
} from '../../services/eoaConfigServices';
import { digitsOnlyError } from './fieldValidation';
import createFormRowId from './formRowId';
import { ensureDraftId } from './persistNestedConfiguration';

export interface PersonRow {
  id: string;
  name: string;
  email: string;
}

export interface NamedRow {
  id: string;
  name: string;
}

export interface EoaaInfoForm {
  yearEnding: string;
  workshopDate: string;
  minCellSize: string;
  districtDataLeads: PersonRow[];
  leadFacilitators: PersonRow[];
  facilitators: PersonRow[];
  additionalLanguageIds: string[];
  staffRoles: NamedRow[];
  estimatedFamilies: string;
  estimatedStudents: string;
  estimatedEducators: string;
}

const ENGLISH_LANGUAGE = 'English';

const additionalSurveyLanguages = (languages: SurveyLanguageOption[]): SurveyLanguageOption[] =>
  languages
    .filter((language) => language.name !== ENGLISH_LANGUAGE)
    .map((language) => ({ ...language, id: String(language.id) }));

const selectedAdditionalLanguageIds = (selectedIds: string[], languages: SurveyLanguageOption[]): string[] => {
  const allowedIds = new Set(additionalSurveyLanguages(languages).map((language) => language.id));
  return selectedIds.map(String).filter((id) => allowedIds.has(id));
};

const additionalLanguagesLabel = (selectedIds: string[], languages: SurveyLanguageOption[]): string => {
  const selected = selectedAdditionalLanguageIds(selectedIds, languages);
  if (selected.length === 0) return 'More languages';
  const namesById = new Map(additionalSurveyLanguages(languages).map((language) => [language.id, language.name]));
  return selected.map((id) => namesById.get(id) ?? id).join(', ');
};

type PersonListKey = 'districtDataLeads' | 'leadFacilitators' | 'facilitators';

const emptyPerson = (): PersonRow => ({ id: createFormRowId('person'), name: '', email: '' });

const emptyNamedRow = (): NamedRow => ({ id: createFormRowId('role'), name: '' });

const PERSON_LIST_KEYS: PersonListKey[] = ['districtDataLeads', 'leadFacilitators', 'facilitators'];

const filterSuggestedUsers = <T extends { name: string; email: string }>(users: T[], query: string): T[] => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return users;
  return users.filter(
    (user) => user.name.toLowerCase().includes(normalized) || user.email.toLowerCase().includes(normalized)
  );
};

const nameForEmail = <T extends { name: string; email: string }>(users: T[], email: string): string | undefined => {
  const normalized = email.trim().toLowerCase();
  if (!normalized) return undefined;
  return users.find((user) => user.email.trim().toLowerCase() === normalized)?.name;
};

const emailsMissingNames = (form: EoaaInfoForm): string[] => {
  const emails = PERSON_LIST_KEYS.flatMap((list) =>
    form[list].filter((person) => person.email.trim() && !person.name.trim()).map((person) => person.email.trim())
  );
  return emails.filter((email, index) => emails.indexOf(email) === index);
};

const applyAssigneeNames = (form: EoaaInfoForm, namesByEmail: Map<string, string>): EoaaInfoForm => {
  let changed = false;
  const nextLists = PERSON_LIST_KEYS.reduce(
    (lists, list) => {
      lists[list] = form[list].map((person) => {
        const name = namesByEmail.get(person.email.trim().toLowerCase());
        if (!name || person.name === name) return person;
        changed = true;
        return { ...person, name };
      });
      return lists;
    },
    {} as Record<PersonListKey, PersonRow[]>
  );
  return changed ? { ...form, ...nextLists } : form;
};

const INITIAL_EOAA_INFO_FORM: EoaaInfoForm = {
  yearEnding: '',
  workshopDate: '',
  minCellSize: '',
  districtDataLeads: [emptyPerson()],
  leadFacilitators: [emptyPerson()],
  facilitators: [emptyPerson()],
  additionalLanguageIds: [],
  staffRoles: [emptyNamedRow()],
  estimatedFamilies: '',
  estimatedStudents: '',
  estimatedEducators: '',
};

const setDigitField = <K extends keyof EoaaInfoForm>(form: EoaaInfoForm, field: K, value: string): EoaaInfoForm => ({
  ...form,
  [field]: value,
});

const academicYearLabel = (yearEnding: number): string => `${yearEnding - 1}-${yearEnding}`;

const academicYearOptions = (referenceYear = new Date().getFullYear()): { value: string; label: string }[] => {
  const start = referenceYear - 6;
  const end = referenceYear + 3;
  return Array.from({ length: end - start + 1 }, (_, index) => {
    const yearEnding = start + index;
    return { value: String(yearEnding), label: academicYearLabel(yearEnding) };
  });
};

const namesFromRows = (rows: NamedRow[]): string[] => rows.map((row) => row.name.trim()).filter(Boolean);

const emailsFromPeople = (people: PersonRow[]): string[] => people.map((person) => person.email.trim()).filter(Boolean);

const peopleFromPayload = (value: string[] | null | undefined): PersonRow[] => {
  const emails = (value ?? []).map((email) => email.trim()).filter(Boolean);
  if (emails.length === 0) return [emptyPerson()];
  return emails.map((email) => ({ ...emptyPerson(), email }));
};

const namedRowsFromPayload = (value: string[] | null | undefined): NamedRow[] => {
  const names = (value ?? []).map((name) => name.trim()).filter(Boolean);
  if (names.length === 0) return [emptyNamedRow()];
  return names.map((name) => ({ ...emptyNamedRow(), name }));
};

const parseOptionalNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const toIsoWorkshopDate = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.includes('T') ? trimmed : `${trimmed}T00:00:00`;
};

const workshopDateFromPayload = (value: string | null | undefined): string => {
  if (!value) return '';
  return value.slice(0, 10);
};

const addPerson = (form: EoaaInfoForm, list: PersonListKey): EoaaInfoForm => ({
  ...form,
  [list]: [...form[list], emptyPerson()],
});

const setPerson = (
  form: EoaaInfoForm,
  list: PersonListKey,
  id: string,
  next: Pick<PersonRow, 'name' | 'email'>
): EoaaInfoForm => ({
  ...form,
  [list]: form[list].map((person) => (person.id === id ? { ...person, ...next } : person)),
});

const addStaffRole = (form: EoaaInfoForm): EoaaInfoForm => ({
  ...form,
  staffRoles: [...form.staffRoles, emptyNamedRow()],
});

const updateStaffRole = (form: EoaaInfoForm, id: string, name: string): EoaaInfoForm => ({
  ...form,
  staffRoles: form.staffRoles.map((role) => (role.id === id ? { ...role, name } : role)),
});

const toEoaaInfoPayload = (form: EoaaInfoForm): EoaInfoConfigurationPayload => {
  return {
    year_ending: form.yearEnding.trim() ? Number(form.yearEnding) : null,
    sjw_date: toIsoWorkshopDate(form.workshopDate),
    min_demographic_bucket_size:
      digitsOnlyError(form.minCellSize) === null ? parseOptionalNumber(form.minCellSize) : null,
    district_data_leads: emailsFromPeople(form.districtDataLeads),
    lead_facilitators: emailsFromPeople(form.leadFacilitators),
    facilitators: emailsFromPeople(form.facilitators),
  };
};

const toSurveyConfigPayload = (
  form: EoaaInfoForm,
  languages: SurveyLanguageOption[] = []
): EoaSurveyConfigurationPayload => ({
  survey_language_ids: selectedAdditionalLanguageIds(form.additionalLanguageIds, languages),
  staff_role_names: namesFromRows(form.staffRoles),
  estimated_families_per_school:
    digitsOnlyError(form.estimatedFamilies) === null ? parseOptionalNumber(form.estimatedFamilies) : null,
  estimated_students_per_school:
    digitsOnlyError(form.estimatedStudents) === null ? parseOptionalNumber(form.estimatedStudents) : null,
  estimated_educators_per_school:
    digitsOnlyError(form.estimatedEducators) === null ? parseOptionalNumber(form.estimatedEducators) : null,
});

const areStringArraysEqual = (left: string[] | null | undefined, right: string[] | null | undefined): boolean =>
  JSON.stringify(left ?? []) === JSON.stringify(right ?? []);

const ROLE_PAYLOAD_KEYS = ['district_data_leads', 'lead_facilitators', 'facilitators'] as const;

const splitRolePayload = (
  payload: EoaInfoConfigurationPayload
): { config: EoaInfoConfigurationPayload; roles: EoaInfoConfigurationPayload } => {
  const config: EoaInfoConfigurationPayload = { ...payload };
  const roles: EoaInfoConfigurationPayload = {};
  ROLE_PAYLOAD_KEYS.forEach((key) => {
    if (key in config) {
      roles[key] = config[key];
      delete config[key];
    }
  });
  return { config, roles };
};

const changedEoaaInfoPayload = (previous: EoaaInfoForm, next: EoaaInfoForm): EoaInfoConfigurationPayload => {
  const previousPayload = toEoaaInfoPayload(previous);
  const nextPayload = toEoaaInfoPayload(next);
  const patch: EoaInfoConfigurationPayload = {};

  if (previousPayload.sjw_date !== nextPayload.sjw_date) {
    patch.sjw_date = nextPayload.sjw_date;
  }
  if (previousPayload.year_ending !== nextPayload.year_ending) {
    patch.year_ending = nextPayload.year_ending;
  }
  if (
    digitsOnlyError(next.minCellSize) === null &&
    previousPayload.min_demographic_bucket_size !== nextPayload.min_demographic_bucket_size
  ) {
    patch.min_demographic_bucket_size = nextPayload.min_demographic_bucket_size;
  }
  if (!areStringArraysEqual(previousPayload.district_data_leads, nextPayload.district_data_leads)) {
    patch.district_data_leads = nextPayload.district_data_leads;
  }
  if (!areStringArraysEqual(previousPayload.lead_facilitators, nextPayload.lead_facilitators)) {
    patch.lead_facilitators = nextPayload.lead_facilitators;
  }
  if (!areStringArraysEqual(previousPayload.facilitators, nextPayload.facilitators)) {
    patch.facilitators = nextPayload.facilitators;
  }

  return patch;
};

const changedSurveyConfigPayload = (
  previous: EoaaInfoForm,
  next: EoaaInfoForm,
  languages: SurveyLanguageOption[] = []
): EoaSurveyConfigurationPayload => {
  const previousPayload = toSurveyConfigPayload(previous, languages);
  const nextPayload = toSurveyConfigPayload(next, languages);
  const patch: EoaSurveyConfigurationPayload = {};

  if (!areStringArraysEqual(previousPayload.survey_language_ids, nextPayload.survey_language_ids)) {
    patch.survey_language_ids = nextPayload.survey_language_ids;
  }
  if (!areStringArraysEqual(previousPayload.staff_role_names, nextPayload.staff_role_names)) {
    patch.staff_role_names = nextPayload.staff_role_names;
  }
  if (
    digitsOnlyError(next.estimatedFamilies) === null &&
    previousPayload.estimated_families_per_school !== nextPayload.estimated_families_per_school
  ) {
    patch.estimated_families_per_school = nextPayload.estimated_families_per_school;
  }
  if (
    digitsOnlyError(next.estimatedStudents) === null &&
    previousPayload.estimated_students_per_school !== nextPayload.estimated_students_per_school
  ) {
    patch.estimated_students_per_school = nextPayload.estimated_students_per_school;
  }
  if (
    digitsOnlyError(next.estimatedEducators) === null &&
    previousPayload.estimated_educators_per_school !== nextPayload.estimated_educators_per_school
  ) {
    patch.estimated_educators_per_school = nextPayload.estimated_educators_per_school;
  }

  return patch;
};

const hasEoaaInfoContent = (form: EoaaInfoForm): boolean => {
  const payload = toEoaaInfoPayload(form);
  return (
    payload.sjw_date !== null ||
    payload.min_demographic_bucket_size !== null ||
    (payload.district_data_leads?.length ?? 0) > 0 ||
    (payload.lead_facilitators?.length ?? 0) > 0 ||
    (payload.facilitators?.length ?? 0) > 0
  );
};

const hasInvalidEoaaInfo = (form: EoaaInfoForm): boolean =>
  digitsOnlyError(form.minCellSize) !== null ||
  digitsOnlyError(form.estimatedFamilies) !== null ||
  digitsOnlyError(form.estimatedStudents) !== null ||
  digitsOnlyError(form.estimatedEducators) !== null;

const hasSurveyConfigContent = (form: EoaaInfoForm, languages: SurveyLanguageOption[] = []): boolean => {
  const payload = toSurveyConfigPayload(form, languages);
  return (
    (payload.survey_language_ids?.length ?? 0) > 0 ||
    (payload.staff_role_names?.length ?? 0) > 0 ||
    payload.estimated_families_per_school !== null ||
    payload.estimated_students_per_school !== null ||
    payload.estimated_educators_per_school !== null
  );
};

const eoaaInfoFromEoa = (
  data: Partial<EoaInfoConfigurationPayload>,
  yearEnding: number | null | undefined
): EoaaInfoForm => ({
  yearEnding: yearEnding === undefined || yearEnding === null ? '' : String(yearEnding),
  workshopDate: workshopDateFromPayload(data.sjw_date),
  minCellSize:
    data.min_demographic_bucket_size === undefined || data.min_demographic_bucket_size === null
      ? ''
      : String(data.min_demographic_bucket_size),
  districtDataLeads: peopleFromPayload(data.district_data_leads),
  leadFacilitators: peopleFromPayload(data.lead_facilitators),
  facilitators: peopleFromPayload(data.facilitators),
  additionalLanguageIds: [],
  staffRoles: [emptyNamedRow()],
  estimatedFamilies: '',
  estimatedStudents: '',
  estimatedEducators: '',
});

const languageIdsFromSurvey = (data: Partial<EoaSurveyConfigurationPayload>): string[] => {
  if (data.survey_language_ids !== undefined && data.survey_language_ids !== null) {
    return data.survey_language_ids.map(String);
  }
  return (data.survey_languages ?? []).map((language) => String(language.id));
};

const surveyConfigFromEoa = (
  data: Partial<EoaSurveyConfigurationPayload>
): Pick<
  EoaaInfoForm,
  'additionalLanguageIds' | 'staffRoles' | 'estimatedFamilies' | 'estimatedStudents' | 'estimatedEducators'
> => ({
  additionalLanguageIds: languageIdsFromSurvey(data),
  staffRoles: namedRowsFromPayload(data.staff_role_names),
  estimatedFamilies:
    data.estimated_families_per_school === undefined || data.estimated_families_per_school === null
      ? ''
      : String(data.estimated_families_per_school),
  estimatedStudents:
    data.estimated_students_per_school === undefined || data.estimated_students_per_school === null
      ? ''
      : String(data.estimated_students_per_school),
  estimatedEducators:
    data.estimated_educators_per_school === undefined || data.estimated_educators_per_school === null
      ? ''
      : String(data.estimated_educators_per_school),
});

const surveyConfigRecordExists = (data: Partial<EoaSurveyConfigurationPayload> | null): boolean =>
  Boolean(
    data &&
      (data.survey_language_ids != null ||
        data.staff_role_names != null ||
        data.estimated_families_per_school != null ||
        data.estimated_students_per_school != null ||
        data.estimated_educators_per_school != null)
  );

const persistEoaaInfo = async ({
  form,
  previousForm,
  eoaId,
  exists,
  createEoaaInfo,
  patchEoaaInfo,
  createDraft,
}: {
  form: EoaaInfoForm;
  previousForm: EoaaInfoForm;
  eoaId: string | null;
  exists: boolean;
  createEoaaInfo: (eoaId: string, payload: EoaInfoConfigurationPayload) => Promise<unknown>;
  patchEoaaInfo: (eoaId: string, payload: EoaInfoConfigurationPayload) => Promise<unknown>;
  createDraft?: () => Promise<string | null>;
}): Promise<boolean> => {
  const draftId = await ensureDraftId({ eoaId, hasContent: hasEoaaInfoContent(form), createDraft });
  if (!draftId) return exists;

  if (!exists) {
    if (!hasEoaaInfoContent(form)) return false;
    const { config, roles } = splitRolePayload(changedEoaaInfoPayload(INITIAL_EOAA_INFO_FORM, form));
    await createEoaaInfo(draftId, config);
    if (Object.keys(roles).length > 0) {
      await patchEoaaInfo(draftId, roles);
    }
    return true;
  }

  const payload = changedEoaaInfoPayload(previousForm, form);
  if (Object.keys(payload).length === 0) return true;

  await patchEoaaInfo(draftId, payload);
  return true;
};

const persistSurveyConfig = async ({
  form,
  previousForm,
  eoaId,
  exists,
  languages = [],
  createSurveyConfig,
  patchSurveyConfig,
  createDraft,
}: {
  form: EoaaInfoForm;
  previousForm: EoaaInfoForm;
  eoaId: string | null;
  exists: boolean;
  languages?: SurveyLanguageOption[];
  createSurveyConfig: (eoaId: string, payload: EoaSurveyConfigurationPayload) => Promise<unknown>;
  patchSurveyConfig: (eoaId: string, payload: EoaSurveyConfigurationPayload) => Promise<unknown>;
  createDraft?: () => Promise<string | null>;
}): Promise<boolean> => {
  const draftId = await ensureDraftId({
    eoaId,
    hasContent: hasSurveyConfigContent(form, languages),
    createDraft,
  });
  if (!draftId) return exists;

  if (!exists) {
    if (!hasSurveyConfigContent(form, languages)) return false;
    await createSurveyConfig(draftId, toSurveyConfigPayload(form, languages));
    return true;
  }

  const payload = changedSurveyConfigPayload(previousForm, form, languages);
  if (Object.keys(payload).length === 0) return true;

  await patchSurveyConfig(draftId, payload);
  return true;
};

export {
  ENGLISH_LANGUAGE,
  additionalLanguagesLabel,
  additionalSurveyLanguages,
  INITIAL_EOAA_INFO_FORM,
  academicYearOptions,
  addPerson,
  addStaffRole,
  applyAssigneeNames,
  changedEoaaInfoPayload,
  changedSurveyConfigPayload,
  eoaaInfoFromEoa,
  emailsMissingNames,
  filterSuggestedUsers,
  hasEoaaInfoContent,
  hasInvalidEoaaInfo,
  nameForEmail,
  persistEoaaInfo,
  persistSurveyConfig,
  surveyConfigRecordExists,
  setDigitField,
  surveyConfigFromEoa,
  toEoaaInfoPayload,
  toSurveyConfigPayload,
  selectedAdditionalLanguageIds,
  setPerson,
  updateStaffRole,
};
